import {Dialog} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {allSeats} from "../../api/trip";
import {useSnackbar} from "notistack";
import {BusSeatPicker} from "./BusSeatPicker";
import LoadingSegment from "../../components/loadingSegment";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import {useFormContext, useWatch} from "react-hook-form";

dayjs.extend(utc);

export default function SeatsDialog({
                                        isReturnSelectionOn,
                                        open,
                                        onClose,
                                        tripData,
                                    }) {

    const {setValue, getValues, control} = useFormContext();
    const {enqueueSnackbar} = useSnackbar();
    const passengers = useWatch({control, name: 'passengers'});
    const [freeSeats, setFreeSeats] = useState({
        oneWaySeats: [],
        returnSeats: []
    });
    const [loading, setLoading] = useState(true)

    const {tripId, departureTripTime, arrStationId, depStationId} = tripData
    const abortControllerRef = useRef(null);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const findFreeSeats = (passedData, returnSeats, signal) => {
        return allSeats(passedData, signal)
            .then(({data}) => {
                setFreeSeats(prev => ({
                    ...prev,
                    [returnSeats ? 'returnSeats' : 'oneWaySeats']: data
                }));
            })
            .catch((err) => {
                if (err?.code === "ERR_CANCELED") return;
                enqueueSnackbar(returnSeats ? "Nema slobodnih mesta za povratak" : "Nema slobodnih mesta za odlazak..", {
                    variant: "info"
                });
            });
    };

    const reloadSeats = () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setLoading(true);
        const calls = [];

        if (isReturnSelectionOn) {
            calls.push(
                findFreeSeats(
                    {
                        id: getValues('returnTrip')?.tripId,
                        date: dayjs.utc(getValues('returnDate')).format('YYYY-MM-DD'),
                        depId: getValues('returnTrip')?.departureStationId,
                        arrId: getValues('returnTrip')?.arrivalStationId
                    },
                    true,
                    controller.signal
                )
            );
        }

        calls.push(
            findFreeSeats(
                {
                    id: tripId,
                    date: dayjs.utc(departureTripTime).format('YYYY-MM-DD'),
                    depId: depStationId,
                    arrId: arrStationId
                },
                false,
                controller.signal
            )
        );

        return Promise.allSettled(calls).finally(() => {
            setLoading(false);

            setFreeSeats(prev => {
                const {oneWaySeats, returnSeats} = prev;

                const updatedPassengers = passengers.map(p => {
                    const oneWayValid = oneWaySeats.find(
                        s =>
                            s.vehicleId === p.vehicleId &&
                            s.seat === p.seat &&
                            s.reservationExpireSec !== null
                    );

                    const returnValid = returnSeats.find(
                        s =>
                            s.vehicleId === p.returnVehicleId &&
                            s.seat === p.returnSeat &&
                            s.reservationExpireSec !== null
                    );

                    return {
                        ...p,
                        seat: oneWayValid ? p.seat : null,
                        vehicleId: oneWayValid ? p.vehicleId : null,
                        ticketId: oneWayValid ? p.ticketId : null,
                        returnSeat: isReturnSelectionOn
                            ? returnValid
                                ? p.returnSeat
                                : null
                            : p.returnSeat,
                        returnVehicleId: isReturnSelectionOn
                            ? returnValid
                                ? p.returnVehicleId
                                : null
                            : p.returnVehicleId,
                        returnTicketId: isReturnSelectionOn
                            ? returnValid
                                ? p.returnTicketId
                                : null
                            : p.returnTicketId
                    };
                });

                setValue('passengers', updatedPassengers);

                return prev;
            });
        });
    };

    useEffect(() => {
        reloadSeats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Dialog fullWidth maxWidth={loading ? "xs" : "xl"} open={open} onClose={onClose}>
            {loading ? (
                <LoadingSegment/>
            ) : (
                <BusSeatPicker
                    isReservation={false}
                    onClose={onClose}
                    passengersList={passengers}
                    freeSeats={freeSeats}
                    tripData={tripData}
                    isReturnSelectionOn={isReturnSelectionOn}
                    onReloadSeats={reloadSeats}
                />
            )}
        </Dialog>
    );
}
